import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEnhancedAddAsset } from "./useEnhancedAddAsset";
import type { AssetType } from "@/types/assets";
import { logger } from "@/services/LoggingService";
import { toast } from "sonner";

// UI Components
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { ASSET_CLASS_ICONS } from "@/constants/investmentIcons";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const formSchema = z.object({
  assetType: z.string(),
  assetId: z.string().min(1, "Please select an asset"),
  // Optional field for equity source selection
  equitySource: z.enum(["stock", "fund"]).optional(),
});

// Asset type options with labels
const ASSET_TYPE_OPTIONS = [
  { value: "equity", label: "Equity" },
  { value: "fixed_income", label: "Fixed Income" },
  { value: "cash", label: "Cash Equivalent" },
  { value: "alternative", label: "Alternative" },
  { value: "multi_asset", label: "Multi-Asset" }
];

// Display label mapping for asset types
const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  'equity': 'Stock',
  'fixed_income': 'Bond',
  'cash': 'Cash Fund',
  'alternative': 'Alternative',
  'multi_asset': 'Multi-Asset Fund'
};

interface EnhancedAddAssetFormProps {
  portfolioId: string;
  onAssetAdded: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  defaultAssetType?: string;
}

export function EnhancedAddAssetForm({
  portfolioId,
  onAssetAdded,
  open,
  setOpen,
  defaultAssetType
}: EnhancedAddAssetFormProps) {
  // Add state for equity source
  const [equitySource, setEquitySource] = useState<"stock" | "fund">("stock");
  const navigate = useNavigate();
  
  // Use our enhanced hook - pass the open prop to ensure state synchronization
  const {
    assets,
    isLoading,
    currentlyAllocatedAssetIds,
    selectedAssetType,
    searchQuery,
    totalRecordCount,
    isLoadingCount,
    handleAssetTypeChange,
    handleSearchQueryChange,
    handleSubmit: submitAsset,
    handleEquitySourceChange
  } = useEnhancedAddAsset(portfolioId, onAssetAdded, open, setOpen, defaultAssetType);

  // React Hook Form setup
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      assetType: selectedAssetType,
      assetId: ""
    },
  });

  // Reset form when asset type changes
  useEffect(() => {
    // Sync form assetType field with hook state
    form.setValue("assetType", selectedAssetType);
    form.setValue("assetId", "");
    
    // Reset equity source to default when asset type changes
    if (selectedAssetType === 'equity') {
      form.setValue("equitySource", "stock");
      setEquitySource("stock");
    } else {
      form.setValue("equitySource", undefined);
    }
  }, [selectedAssetType, form]);

  // Get placeholder text based on asset type
  const getSearchPlaceholder = (assetType: AssetType) => {
    switch(assetType) {
      case 'equity':
        return "Search by stock name or ticker...";
      case 'fixed_income':
        return "Search by bond fund name or code...";
      case 'cash':
        return "Search by cash fund name or code...";
      case 'alternative':
        return "Search by alternative fund name or code...";
      case 'multi_asset':
        return "Search by multi-asset fund name or code...";
      default:
        return "Search by name or code...";
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await submitAsset({ assetId: values.assetId });
      toast.success("Asset added successfully");
    } catch (error) {
      // Error toast is already handled by the hook
    }
  };
  
  // Handle equity source change
  const onEquitySourceChange = (value: "stock" | "fund") => {
    logger.debug('EnhancedAddAssetForm', 'Equity source changed', { value });
    setEquitySource(value);
    form.setValue("equitySource", value);
    handleEquitySourceChange(value);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="assetType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Filter by Type</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  handleAssetTypeChange(value as AssetType);
                }}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select asset type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ASSET_TYPE_OPTIONS.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        {selectedAssetType === 'equity' && (
          <FormField
            control={form.control}
            name="equitySource"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Source</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={(value) => {
                      field.onChange(value);
                      onEquitySourceChange(value as "stock" | "fund");
                    }}
                    value={field.value ?? "stock"}
                    className="flex flex-row space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="stock" id="stock" />
                      <Label htmlFor="stock">Ticker Symbol</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="fund" id="fund" />
                      <Label htmlFor="fund">Fund Code</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        
        <FormItem>
          <div className="flex items-center justify-between">
            <FormLabel className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Search
            </FormLabel>
            {/* Show record count for all asset types */}
            <span className="text-xs text-muted-foreground">
              {isLoadingCount ? (
                "Loading..."
              ) : totalRecordCount > 0 ? (
                `${totalRecordCount.toLocaleString()} records`
              ) : (
                "0 records"
              )}
            </span>
          </div>
          <Input
            type="text"
            placeholder={getSearchPlaceholder(selectedAssetType as AssetType)}
            value={searchQuery}
            onChange={(e) => handleSearchQueryChange(e.target.value)}
            className="w-full"
          />
        </FormItem>

        <FormField
          control={form.control}
          name="assetId"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                {(() => {
                  // Get the appropriate icon based on asset class
                  const getAssetClassIcon = () => {
                    switch(selectedAssetType) {
                      case 'equity':
                        return equitySource === "stock" ? ASSET_CLASS_ICONS.equity : ASSET_CLASS_ICONS.alternative;
                      case 'fixed_income':
                        return ASSET_CLASS_ICONS.fixed_income;
                      case 'cash':
                        return ASSET_CLASS_ICONS.cash;
                      case 'alternative':
                        return ASSET_CLASS_ICONS.alternative;
                      case 'multi_asset':
                        return ASSET_CLASS_ICONS.multi_asset;
                      default:
                        return ASSET_CLASS_ICONS.unclassified;
                    }
                  };
                  
                  const IconComponent = getAssetClassIcon();
                  const label = selectedAssetType === 'equity'
                    ? 'Select an Equity'
                    : `Select a ${ASSET_TYPE_LABELS[selectedAssetType as AssetType]}`;
                  
                  return (
                    <>
                      <IconComponent className="h-4 w-4" />
                      {label}
                    </>
                  );
                })()}
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue 
                      placeholder="Select an Asset"
                      className="truncate"
                    >
                      {field.value && (() => {
                        const selectedAsset = assets.find(asset => asset.id === field.value);
                        if (!selectedAsset) return null;
                        return (
                          <div className="flex items-center w-full">
                            <span className="text-xs font-mono text-muted-foreground whitespace-nowrap w-16 flex-shrink-0">
                              {selectedAsset.source === 'funds_raw' && selectedAsset.managementCompanyCode 
                                ? `${selectedAsset.managementCompanyCode} ${selectedAsset.code}` 
                                : selectedAsset.code
                              }
                            </span>
                            <span className="text-xs truncate flex-1 min-w-0 pl-2">
                              {selectedAsset.name}
                            </span>
                          </div>
                        );
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-96 overflow-y-auto max-w-[480px] w-full">
                  {isLoading ? (
                    <div className="flex items-center justify-center p-4">
                      <Spinner size="md" ariaLabel="Loading assets" />
                    </div>
                  ) : (assets?.length || 0) === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      No assets found. Try a different search.
                    </div>
                  ) : (
                    <>
                      {assets.map(asset => (
                        <SelectItem 
                          key={asset.id} 
                          value={asset.id} 
                          className={`${
                            currentlyAllocatedAssetIds.has(asset.id) ? 'opacity-50' : ''
                          } px-1 py-1`}
                        >
                          <div className="flex items-center w-full pl-6">
                            <span 
                              className="text-xs font-mono text-muted-foreground whitespace-nowrap w-16 flex-shrink-0"
                            >
                              {asset.source === 'funds_raw' && asset.managementCompanyCode 
                                ? `${asset.managementCompanyCode} ${asset.code}` 
                                : asset.code
                              }
                            </span>
                            <span className="text-xs truncate flex-1 min-w-0 pl-2">
                              {asset.name}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                      
                      {/* Invitation to advanced search */}
                      <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-center gap-3">
                          <Search className="h-5 w-5 text-gray-500" />
                          <div className="flex flex-col items-center">
                            <div className="text-xs text-gray-600 mb-1">
                              Looking for more assets?
                            </div>
                            <div className="flex gap-2">
                              <button 
                                type="button"
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                                onClick={() => {
                                  // Close dialog and navigate to explore page with all-stocks tab
                                  setOpen(false);
                                  navigate('/explore?tab=all-stocks');
                                }}
                              >
                                All Stocks
                              </button>
                              <span className="text-xs text-gray-400">•</span>
                              <button 
                                type="button"
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                                onClick={() => {
                                  // Close dialog and navigate to explore page with all-funds tab
                                  setOpen(false);
                                  navigate('/explore?tab=all-funds');
                                }}
                              >
                                All Funds
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button 
          type="submit" 
          className="w-full"
          disabled={isLoading || !form.formState.isValid}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" ariaLabel="Loading" />
              Loading...
            </>
          ) : (
            "Add Asset"
          )}
        </Button>
      </form>
    </Form>
  );
}
